import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  PublicQuoteView,
  QuoteApprovalPlacement,
  QuoteLinesFrequency,
  QuoteTemplate,
  UpdateQuoteTemplateInput,
} from '@catalyst-psa/shared';

import { mutationError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { SelectField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import {
  collectNoteImageIds,
  hydrateNoteImages,
  serializeNoteImages,
} from '../../components/ui/RichText.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import {
  resolveQuoteTemplateImages,
  uploadQuoteTemplateImage,
  useDeleteQuoteTemplate,
  usePreviewQuoteTemplateDeliveryView,
  usePreviewQuoteTemplatePdf,
  useQuoteTemplate,
  useUpdateQuoteTemplate,
} from './api.js';
import { QuoteDeliveryView } from './QuoteDeliveryView.js';
import {
  QuoteLinesEditor,
  defaultLineLabels,
  toLineForms,
  toLineInputs,
  toLineLabelInputs,
  toLineLabelsForm,
  type LineForm,
  type LineLabelsForm,
} from './QuoteLinesEditor.js';
import {
  QuoteSectionsEditor,
  toSectionForms,
  toSectionInputs,
  type SectionForm,
} from './QuoteSectionsEditor.js';

export function QuoteTemplateDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuoteTemplate(id);
  const template = query.data;

  // Inline pictures are stored as stable `psa-image:{id}` refs; resolve them to fresh
  // display URLs BEFORE mounting the editor (its state initializes from props).
  const imageIds = useMemo(() => {
    const set = new Set<string>();
    for (const section of template?.sections ?? []) {
      if (section.html) {
        for (const imageId of collectNoteImageIds(section.html)) set.add(imageId);
      }
    }
    return [...set].sort();
  }, [template]);
  const imagesQuery = useQuery({
    queryKey: ['quote-templates', 'images', template?.id, imageIds],
    queryFn: () => resolveQuoteTemplateImages(template?.id ?? '', imageIds),
    enabled: Boolean(template) && imageIds.length > 0,
  });
  const imageUrls = imagesQuery.data ?? {};

  const hydrated = useMemo<QuoteTemplate | null>(() => {
    if (!template) return null;
    return {
      ...template,
      sections: (template.sections ?? []).map((section) => ({
        ...section,
        html: section.html ? hydrateNoteImages(section.html, imageUrls) : section.html,
      })),
    };
  }, [template, imageUrls]);

  const waitingForImages = imageIds.length > 0 && imagesQuery.isLoading;

  return (
    <div>
      <DataState
        isLoading={query.isLoading || waitingForImages}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        isEmpty={false}
      >
        {hydrated && !waitingForImages ? (
          <TemplateEditor key={hydrated.id} template={hydrated} />
        ) : null}
      </DataState>
    </div>
  );
}

function TemplateEditor({ template }: { template: QuoteTemplate }): JSX.Element {
  const navigate = useNavigate();
  const categoriesQuery = useServiceCategoryOptions();

  const [name, setName] = useState(template.name);
  const [opportunityTypeId, setOpportunityTypeId] = useState(template.opportunityTypeId ?? '');
  const [isActive, setIsActive] = useState(template.isActive);
  const [linesAfterSection, setLinesAfterSection] = useState(String(template.linesAfterSection));
  const [lines2AfterSection, setLines2AfterSection] = useState(
    String(template.lines2AfterSection),
  );
  const [lines1Frequency, setLines1Frequency] = useState<QuoteLinesFrequency>(
    template.lines1Frequency,
  );
  const [lines2Frequency, setLines2Frequency] = useState<QuoteLinesFrequency>(
    template.lines2Frequency,
  );
  const [lines1BreakBefore, setLines1BreakBefore] = useState(template.lines1PageBreakBefore);
  const [lines1BreakAfter, setLines1BreakAfter] = useState(template.lines1PageBreakAfter);
  const [lines2BreakBefore, setLines2BreakBefore] = useState(template.lines2PageBreakBefore);
  const [lines2BreakAfter, setLines2BreakAfter] = useState(template.lines2PageBreakAfter);
  const [lines1Labels, setLines1Labels] = useState<LineLabelsForm>(() =>
    toLineLabelsForm(
      {
        sectionLabel: template.lines1SectionLabel,
        qtyLabel: template.lines1QtyLabel,
        unitPriceLabel: template.lines1UnitPriceLabel,
        amountLabel: template.lines1AmountLabel,
        totalLabel: template.lines1TotalLabel,
        qtyHidden: template.lines1QtyHidden,
        unitPriceHidden: template.lines1UnitPriceHidden,
        amountHidden: template.lines1AmountHidden,
        totalHidden: template.lines1TotalHidden,
      },
      template.lines1Frequency,
    ),
  );
  const [lines2Labels, setLines2Labels] = useState<LineLabelsForm>(() =>
    toLineLabelsForm(
      {
        sectionLabel: template.lines2SectionLabel,
        qtyLabel: template.lines2QtyLabel,
        unitPriceLabel: template.lines2UnitPriceLabel,
        amountLabel: template.lines2AmountLabel,
        totalLabel: template.lines2TotalLabel,
        qtyHidden: template.lines2QtyHidden,
        unitPriceHidden: template.lines2UnitPriceHidden,
        amountHidden: template.lines2AmountHidden,
        totalHidden: template.lines2TotalHidden,
      },
      template.lines2Frequency,
    ),
  );
  const [approvalPlacement, setApprovalPlacement] = useState<QuoteApprovalPlacement>(
    template.approvalPlacement,
  );
  const [sections, setSections] = useState<SectionForm[]>(() => toSectionForms(template.sections));
  const [lines, setLines] = useState<LineForm[]>(() => toLineForms(template.lines));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deliveryPreview, setDeliveryPreview] = useState<PublicQuoteView | null>(null);

  const updateMutation = useUpdateQuoteTemplate();
  const deleteMutation = useDeleteQuoteTemplate();
  const previewMutation = usePreviewQuoteTemplatePdf();
  const deliveryPreviewMutation = usePreviewQuoteTemplateDeliveryView();

  // Changing a group's frequency follows the default section/total labels along (only
  // when the user hasn't customized them).
  const changeLines1Frequency = (next: QuoteLinesFrequency): void => {
    setLines1Labels((prev) => {
      const oldDefaults = defaultLineLabels(lines1Frequency);
      const newDefaults = defaultLineLabels(next);
      return {
        ...prev,
        header: prev.header === oldDefaults.header ? newDefaults.header : prev.header,
        total: prev.total === oldDefaults.total ? newDefaults.total : prev.total,
      };
    });
    setLines1Frequency(next);
  };
  const changeLines2Frequency = (next: QuoteLinesFrequency): void => {
    setLines2Labels((prev) => {
      const oldDefaults = defaultLineLabels(lines2Frequency);
      const newDefaults = defaultLineLabels(next);
      return {
        ...prev,
        header: prev.header === oldDefaults.header ? newDefaults.header : prev.header,
        total: prev.total === oldDefaults.total ? newDefaults.total : prev.total,
      };
    });
    setLines2Frequency(next);
  };

  /** The full template payload from the current (possibly unsaved) editor state. */
  const buildInput = (): UpdateQuoteTemplateInput => {
    const labels1 = toLineLabelInputs(lines1Labels, lines1Frequency);
    const labels2 = toLineLabelInputs(lines2Labels, lines2Frequency);
    return {
      name: name.trim(),
      opportunityTypeId: opportunityTypeId || null,
      isActive,
      linesAfterSection: Number(linesAfterSection),
      lines2AfterSection: Number(lines2AfterSection),
      lines1Frequency,
      lines2Frequency,
      lines1PageBreakBefore: lines1BreakBefore,
      lines1PageBreakAfter: lines1BreakAfter,
      lines2PageBreakBefore: lines2BreakBefore,
      lines2PageBreakAfter: lines2BreakAfter,
      lines1QtyLabel: labels1.qtyLabel,
      lines1UnitPriceLabel: labels1.unitPriceLabel,
      lines1AmountLabel: labels1.amountLabel,
      lines1TotalLabel: labels1.totalLabel,
      lines2QtyLabel: labels2.qtyLabel,
      lines2UnitPriceLabel: labels2.unitPriceLabel,
      lines2AmountLabel: labels2.amountLabel,
      lines2TotalLabel: labels2.totalLabel,
      lines1QtyHidden: labels1.qtyHidden,
      lines1UnitPriceHidden: labels1.unitPriceHidden,
      lines1AmountHidden: labels1.amountHidden,
      lines1TotalHidden: labels1.totalHidden,
      lines2QtyHidden: labels2.qtyHidden,
      lines2UnitPriceHidden: labels2.unitPriceHidden,
      lines2AmountHidden: labels2.amountHidden,
      lines2TotalHidden: labels2.totalHidden,
      lines1SectionLabel: labels1.sectionLabel,
      lines2SectionLabel: labels2.sectionLabel,
      approvalPlacement,
      // Store the stable `psa-image:{id}` refs instead of the volatile display URLs.
      sections: toSectionInputs(
        sections.map((s) => ({ ...s, html: serializeNoteImages(s.html) })),
      ),
      lines: toLineInputs(lines),
    };
  };

  const handleSave = (): void => {
    updateMutation.mutate({ id: template.id, input: buildInput() });
  };

  const invalidLines = lines.some((line) => !line.description.trim());
  // Surface preview failures too — otherwise a rejected preview (e.g. missing permission)
  // looks like the button simply did nothing.
  const error =
    mutationError(updateMutation.error, 'Failed to save the template.') ??
    mutationError(previewMutation.error, 'Failed to render the PDF preview.') ??
    mutationError(deliveryPreviewMutation.error, 'Failed to render the delivery page preview.');

  return (
    <div>
      <Link
        to="/admin/quote-templates"
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        ← Back to quote templates
      </Link>

      <PageHeader
        title={template.name}
        description="Sections and lines applied to a quote when this template is selected."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="text-rose-600 hover:bg-rose-50"
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
            <Button variant="secondary" onClick={() => setHistoryOpen(true)}>
              History
            </Button>
            <Button
              variant="secondary"
              isLoading={previewMutation.isPending}
              disabled={invalidLines}
              title="Renders the current editor content (saved or not) with a sample customer header"
              onClick={() => previewMutation.mutate(buildInput())}
            >
              Preview PDF
            </Button>
            <Button
              variant="secondary"
              isLoading={deliveryPreviewMutation.isPending}
              disabled={invalidLines}
              title="Shows the public delivery page as the customer would see it, from the current editor content"
              onClick={() =>
                deliveryPreviewMutation.mutate(buildInput(), {
                  onSuccess: (view) => setDeliveryPreview(view),
                })
              }
            >
              Preview delivery page
            </Button>
            <Button
              isLoading={updateMutation.isPending}
              disabled={!name.trim() || invalidLines}
              onClick={handleSave}
            >
              Save template
            </Button>
          </div>
        }
      />

      {error ? (
        <p className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      ) : null}
      {updateMutation.isSuccess ? (
        <p className="mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Template saved.
        </p>
      ) : null}
      {invalidLines ? (
        <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Every line needs a description before saving.
        </p>
      ) : null}

      <div className="mb-6 grid gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-3">
        <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
        <SelectField
          label="Opportunity Type"
          value={opportunityTypeId}
          options={[
            { value: '', label: 'Any type' },
            ...(categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
          ]}
          onChange={(e) => setOpportunityTypeId(e.target.value)}
        />
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Active (available to apply)
        </label>
      </div>

      <QuoteSectionsEditor
        sections={sections}
        onChange={setSections}
        slot="numbered"
        onUploadImage={(file) => uploadQuoteTemplateImage(template.id, file)}
      />

      <QuoteLinesEditor
        lines={lines}
        onChange={setLines}
        groups={[
          {
            frequency: lines1Frequency,
            placement: linesAfterSection,
            pageBreakBefore: lines1BreakBefore,
            pageBreakAfter: lines1BreakAfter,
            labels: lines1Labels,
            onFrequencyChange: changeLines1Frequency,
            onPlacementChange: setLinesAfterSection,
            onPageBreakBeforeChange: setLines1BreakBefore,
            onPageBreakAfterChange: setLines1BreakAfter,
            onLabelsChange: (patch) => setLines1Labels((prev) => ({ ...prev, ...patch })),
          },
          {
            frequency: lines2Frequency,
            placement: lines2AfterSection,
            pageBreakBefore: lines2BreakBefore,
            pageBreakAfter: lines2BreakAfter,
            labels: lines2Labels,
            onFrequencyChange: changeLines2Frequency,
            onPlacementChange: setLines2AfterSection,
            onPageBreakBeforeChange: setLines2BreakBefore,
            onPageBreakAfterChange: setLines2BreakAfter,
            onLabelsChange: (patch) => setLines2Labels((prev) => ({ ...prev, ...patch })),
          },
        ]}
        approval={{ placement: approvalPlacement, onChange: setApprovalPlacement }}
      />

      {/* The Legal Contract always edits at the very bottom, below the line sections. */}
      <div className="mt-6">
        <QuoteSectionsEditor
          sections={sections}
          onChange={setSections}
          slot="legal"
          onUploadImage={(file) => uploadQuoteTemplateImage(template.id, file)}
        />
      </div>

      <Modal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={`History — ${template.name}`}
        size="lg"
        fillHeight
      >
        <HistoryPanel entityType="QuoteTemplate" entityId={template.id} enabled={historyOpen} />
      </Modal>

      <Modal
        isOpen={deliveryPreview !== null}
        onClose={() => setDeliveryPreview(null)}
        title="Delivery page preview"
        size="wide"
        fillHeight
      >
        {deliveryPreview ? (
          <div className="rounded-xl bg-slate-100 p-6">
            <QuoteDeliveryView view={deliveryPreview} previewMode />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete quote template"
        message={`Delete "${template.name}"? Quotes it was already applied to keep their content.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={() =>
          deleteMutation.mutate(template.id, {
            onSuccess: () => navigate('/admin/quote-templates'),
          })
        }
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
